import type { ReactNode } from 'react';
import clsx from 'clsx';

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('rounded-[var(--radius-card)] bg-surface p-4', className)}>{children}</div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between px-4">
      <h2 className="text-[0.8125rem] text-muted">{children}</h2>
      {action}
    </div>
  );
}

/** Riquadro compatto con un numero: Spese, Guadagno, Risparmio. */
export function Tile({
  label,
  value,
  tone = 'neutral',
  strip = false,
  valueLg,
}: {
  label: string;
  value: string;
  /** Valore alternativo da 1024px in su (es. con i centesimi). */
  valueLg?: string;
  tone?: 'neutral' | 'positive' | 'negative';
  /** Riga di totali della Home: valore sopra l'etichetta su telefono, sotto da desktop. */
  strip?: boolean;
}) {
  return (
    <div
      className={clsx(
        'px-2',
        strip
          ? 'flex flex-col-reverse items-center lg:flex-col lg:items-start lg:px-0'
          : 'text-center',
      )}
    >
      <div className="text-xs text-muted">{label}</div>
      <div
        className={clsx(
          'tnum mt-0.5 text-xl font-semibold tracking-tight',
          strip && 'lg:text-2xl',
          tone === 'positive' && 'text-success',
          tone === 'negative' && 'text-danger',
        )}
      >
        {valueLg ? (
          <>
            <span className="lg:hidden">{value}</span>
            <span className="hidden lg:inline">{valueLg}</span>
          </>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

/**
 * Calibro del budget: la parte piena e' il speso, il segno e' il ritmo ideale
 * di oggi. Il colore segue il consumo: accento, ambra se si e' oltre il ritmo,
 * rosso oltre il budget.
 */
export function Gauge({ ratio, pace }: { ratio: number; pace: number }) {
  const color =
    ratio > 1 ? 'var(--danger)' : ratio > pace ? 'var(--warning-fill)' : 'var(--accent)';
  const fill = Math.max(0, Math.min(ratio, 1)) * 100;
  const mark = Math.max(0, Math.min(pace, 1)) * 100;
  return (
    <div
      role="img"
      aria-label={`${Math.round(ratio * 100)}% del budget speso, ritmo ideale ${Math.round(pace * 100)}%`}
      className="relative h-2.5 w-full rounded-full bg-surface-2"
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${fill}%`, background: color }}
      />
      <span
        className="absolute -top-1 -bottom-1 w-[3px] -translate-x-1/2 rounded-sm bg-text"
        style={{ left: `${mark}%`, boxShadow: '0 0 0 2px var(--bg)' }}
        aria-hidden="true"
      />
    </div>
  );
}

/** Barra orizzontale usata dalla ripartizione per categoria e dai budget. */
export function Bar({ ratio, color }: { ratio: number; color: string }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-[2px] bg-surface-2">
      <div
        className="h-full rounded-[2px] transition-[width] duration-500"
        style={{ width: `${Math.max(2, Math.min(ratio, 1) * 100)}%`, background: color }}
      />
    </div>
  );
}

export function Dot({ color, small = false }: { color: string; small?: boolean }) {
  return (
    <span
      className={clsx('inline-block shrink-0 rounded-full', small ? 'h-2 w-2' : 'h-2.5 w-2.5')}
      style={{ background: color }}
      aria-hidden="true"
    />
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <p className="font-medium">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function Spinner({ label = 'Caricamento' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-accent" />
      {label}
    </div>
  );
}

export function Button({
  children,
  variant = 'primary',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' }) {
  return (
    <button
      {...props}
      className={clsx(
        'rounded-[var(--radius-control)] px-4 py-3 text-[1.0625rem] font-semibold transition active:scale-[0.98] disabled:opacity-50',
        variant === 'primary' && 'bg-accent text-accent-text',
        variant === 'ghost' && 'bg-surface-2 text-accent',
        variant === 'danger' && 'bg-danger-soft text-danger',
        className,
      )}
    >
      {children}
    </button>
  );
}
